import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {CerfService} from '../../services/cerf/cerf.service';
import {ActivatedRoute, Router} from '@angular/router';
import {BreadcrumbService} from 'angular-crumbs';
import {FormBuilder, FormGroup} from '@angular/forms';
import {createCerfForm} from './cerf-details-form.';
import {CerfDetails} from '../../interfaces/CerfDetails';
import {FormUtilities} from '../../../utilities/FormUtilities';
import {UserModel} from '../../../user/models/UserModel';
import {CurrentUserService} from '../../../user/services/current-user/current-user.service';
import {MdDialog, MdSnackBar} from '@angular/material';
import {CerfConfirmationDialogComponent} from '../cerf-confirmation-dialog/cerf-confirmation-dialog.component';
import {ECerfStatus} from '../../interfaces/ECerfStatus';
import {currencyMask, getLocalDateTimeString, numberFormatter} from '../../../utilities/utils';
import {AirportService} from '../../../airport/services/airport/airport.service';

@Component({
  selector: 'app-cerf-form',
  templateUrl: './cerf-form.component.html',
  styleUrls: ['./cerf-form.component.scss']
})
export class CerfFormComponent implements OnInit {

  @Input() label: string;
  @Input() cerf: CerfDetails;
  @Input() originalExistingFiles = [];
  existingFiles = [];
  newFiles = [];
  @Input() cerfId?: number;
  cerfForm: FormGroup;
  editable: boolean;
  submitting: boolean;
  currencyMask = { mask: currencyMask };
  allowedFileTypes = ['.pdf', '.xlsx', 'xls', '.csv', '.png', '.jpg', '.jpeg', '.docx', '.rar', '.zip', '.msg', '.txt'];
  unsafeCharacters = ['"', '<', '>', '%', '{', '}', '|', '\\', '^', '~', '[', ']', '`', ';', '?', ':', '@', '=', '&', '/'];
  airports = [];

  @ViewChild('description')
  private descriptionElement: ElementRef;
  @ViewChild('cerfFileInput')
  private fileInput: ElementRef;
  private currentUser: UserModel;


  constructor(
    public dialog: MdDialog,
    private route: ActivatedRoute,
    private cerfService: CerfService,
    private userService: CurrentUserService,
    private airportService: AirportService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private formBuilder: FormBuilder,
    public formUtils: FormUtilities,
    public snackBar: MdSnackBar) {
    this.unsafeCharacters = '\"<>%\{\}\|\\\^~\[\]`;\?:@=&/'.split('');
    if (!this.label) {
      this.label = 'New CERF'
    }
    this.addComment = this.addComment.bind(this);
    this.userService.getCurrentUser().subscribe((user) => {
      this.currentUser = user;
    });
    this.airportService.getAllAirportsForCerf().then((airports) => {
      this.airports = airports;
    });
  }

  ngOnInit() {
    this.existingFiles = new Array(...this.originalExistingFiles);
    this.editable = false;
    this.submitting = false;
    if (!this.cerf) {
      this.editable = true;
    }
    this.createForm(this.cerf || {});
    this.descriptionElement.nativeElement.style.height = '150px';
  }

  createForm(formValues: CerfDetails = null) {
    this.cerfForm = createCerfForm(this.formBuilder, formValues, !this.canEditForm, this.canEditCerfNumber);
  }

  get canEditCerfNumber() {
    return !(this.editable && this.userCanEditCerfNumber())
  }

  get canEditForm() {
    if (this.userCanEditCerfNumber())
      return false;
    return this.editable;
  }

  isInvalidAndTouched(formControlName: string) {
    return this.formUtils.isInvalidAndTouched(this.cerfForm, formControlName);
  }

  hasError(error: string) {
    return !!this.cerfForm.hasError(error);
  }

  sanitizeCurrencyAndGetTotal(values: string[]) {
    let total = 0;
    values.forEach((value: string) => {
      if (value) {
        // The toFixed then slice eliminates a rounding error cause by 3 decimals: ex { 1.888 rounds to 1.89}
        // Also removes any non numeric characters while keeping the . for decimals
        const cost = Number(value.replace('$', '').replace(/,/g, '').replace(/[^\d.-]/g, '')).toFixed(3).slice(0, -1);
        total = total + Number(cost);
      }
    });
    return total;
  }

  setTotalCost() {
    const values: CerfDetails = this.cerfForm.value;
    const totalCost = this.sanitizeCurrencyAndGetTotal([values.basic_costs, values.other_costs, values.freight_costs, values.taxes]);
    this.cerfForm.controls['total_costs'].setValue(numberFormatter.format(totalCost));
  }

  setTotalExpenditureCosts() {
    const values: CerfDetails = this.cerfForm.value;
    const totalCost = this.sanitizeCurrencyAndGetTotal([
      values.january_march_expenditure_costs,
      values.april_june_expenditure_costs,
      values.july_september_expenditure_costs,
      values.october_december_expenditure_costs,
      values.next_year_expenditure_costs
    ]);
    this.cerfForm.controls['total_expenditure_costs'].setValue(numberFormatter.format(totalCost));
  }

  onSubmit() {
    this.submitting = true;
    this.cerfId ? this.updateCerf() :  this.saveCerf();
  }

  async saveCerf() {
    try {
      const result = await this.cerfService.create(this.cerfForm.getRawValue(), this.newFiles);
      this.submitting = false;
      this.snackBar.open(result['_body'], 'Dismiss', {duration: 3000});
      await this.router.navigate(['dashboard']);
    } catch (e) {
      this.submitting = false;
      this.snackBar.open('No station manager exists for selected business unit', 'Dismiss', {duration: 3000});
    }
  }

  async updateCerf() {
    const filesToRemove = this.originalExistingFiles.filter(x => !this.existingFiles.includes(x)).map((fileDetail => fileDetail[0]));
    this.cerfService.update(this.cerfForm.getRawValue(), this.newFiles, filesToRemove, this.cerfId).then((res) => {
      this.cerf = res;
      this.originalExistingFiles = new Array(...res.existingFiles);
      this.existingFiles = new Array(...this.originalExistingFiles);
      this.newFiles = [];
      this.editable = false;
      this.createForm(this.cerf);
      this.submitting = false;
      this.snackBar.open('CERF Saved.', 'Dismiss', { duration: 3000 });
    });
  }

  onEdit() {
    this.editable = true;
    this.createForm(this.cerf);
  }

  onCancel() {
    this.editable = false;
    this.createForm(this.cerf);
    this.existingFiles = new Array(...this.originalExistingFiles);
    this.newFiles = [];
  }

  async approve() {
    const result = await this.openCerfConfirmationDialog('Describe the reason CERF request was approved.');
    // A user could type false into the comment but this is an edge case that is not really an issue.
    if (result !== 'false' && result.length > 0) {
      await this.addComment(result);
    }
    const res = await this.cerfService.approveCerfRequest(this.cerfId);
    this.snackBar.open(res['_body'], 'Dismiss', { duration: 3000 });
    await this.router.navigate(['dashboard']);
  }

  async decline() {
    const result = await this.openCerfConfirmationDialog('Describe the reason CERF request was declined.');
    if (result !== 'false' && result.length > 0) {
      await this.addComment(result);
    }
    const res = await this.cerfService.declineCerfRequest(this.cerfId);
    this.snackBar.open(res['_body'], 'Dismiss', { duration: 3000 });
    await this.router.navigate(['dashboard']);
  }

   async openCerfConfirmationDialog(message) {
    return await this.dialog.open(CerfConfirmationDialogComponent, { data: {
        message: message
      }}).afterClosed().toPromise();
  }


  exportToPdf() {
    window.location.href = this.cerfService.downloadPdfUrl(this.cerf.id);
  }

  async addComment(text: string) {
    if (!text) return;
    const comment = await this.cerfService.addComment(this.cerf.id, text);
    if (!this.cerf.comments) {
      this.cerf.comments = [];
    }
    this.cerf.comments.push(comment);
  };


  onSelectFile(event) {
    console.log(this.fileInput);
    if (event.target.files && event.target.files[0]) {
      for (const file of event.target.files) {
        for (const character of file.name) {
          if (this.unsafeCharacters.indexOf(character) !== -1) {
            this.snackBar.open(
              `${character} is not an allowed character.`,
              'Dismiss', { duration: 3000 });
            this.fileInput.nativeElement.value = '';
            return;
          }
        }
        if (this.allowedFileTypes.indexOf(`.${file.name.split('.').pop()}`, 0) !== -1) {
          this.newFiles.push(file);
        } else {
          this.snackBar.open(file.name + ' is not an allowed file type.', 'Dismiss', { duration: 3000 });
          this.fileInput.nativeElement.value = '';
        }
      }
    }
  }

  removeFile(index) {
    if (this.existingFiles.length >= index)
      this.existingFiles.splice(index, 1);
  }

  removeNewFile(index) {
    if (this.newFiles.length >= index)
      this.newFiles.splice(index, 1);
  }

  downloadFile(fileName) {
      window.open(this.cerfService.getFileUrl(this.cerfId, fileName));
  }

  userCanApprove() {
    return this.currentUser['cerf_level'] >= this.cerf.level && this.cerf.status !== ECerfStatus.FULLY_APPROVED ||
      this.currentUser['cerf_level'] === -1 && this.cerf.level === 5;
  }

  userCanDecline() {
    return this.currentUser['cerf_level'] >= this.cerf.level && this.cerf.status === ECerfStatus.PENDING_APPROVAL ||
      this.currentUser['cerf_level'] === -1 && this.cerf.level === 5;
  }

  userCanEditCerfNumber() {
    if (!this.cerf)
      return false;
    return !!Number(this.currentUser['cerf_level'] === 0 && this.cerf.status === ECerfStatus.FULLY_APPROVED
      || this.currentUser.can('edit-cerf-number') && this.cerf.status === ECerfStatus.FULLY_APPROVED);
  }

  convertDateToLocalTime(date) {
    return getLocalDateTimeString(date);
  }

}



// WEBPACK FOOTER //
// /var/www/dnata-cerf-front-end/releases/20200326161233/src/app/modules/cerf/components/cerf-form/cerf-form.component.ts